import { formatDelta, directionOf } from '@/lib/format';
import styles from './Delta.module.css';

export interface DeltaProps {
  /** Percentage-point change, already expressed as a percentage (-0.07 = -0.07%). */
  value: number;
  /** Suffix shown after the number, e.g. "QoQ". */
  suffix?: string;
  digits?: number;
  /**
   * When true, a falling value is the good outcome (churn, cost).
   * Only affects colour — the arrow always follows the actual direction.
   */
  invert?: boolean;
  size?: 'sm' | 'md';
  /** Above this magnitude show ">+max%" (exact value in the title) — a near-zero
   *  base turns into five-figure noise that reads as broken. */
  max?: number;
}

/**
 * QoQ movement indicator. Arrow + sign + colour, so direction never relies on
 * colour alone (DESIGN.md accessibility rule).
 */
export function Delta({ value, suffix, digits = 1, invert = false, size = 'sm', max }: DeltaProps) {
  const direction = directionOf(value, 10 ** -digits / 2);
  const good = invert ? direction === 'down' : direction === 'up';
  const tone = direction === 'flat' ? 'flat' : good ? 'good' : 'bad';
  const capped = max !== undefined && Math.abs(value) > max;

  return (
    <span
      className={`${styles.delta} ${styles[tone]} ${styles[size]}`}
      title={capped ? `${formatDelta(value, 0)} — previous period was near zero` : undefined}
    >
      <span className={styles.arrow} aria-hidden="true">
        {direction === 'up' ? '▲' : direction === 'down' ? '▼' : '—'}
      </span>
      {capped ? `>${value > 0 ? '+' : '−'}${max}%` : formatDelta(value, digits)}
      {suffix && <span className={styles.suffix}>{suffix}</span>}
    </span>
  );
}
